import {
	Card,
	Checkbox,
	Col,
	Flex,
	Form,
	Row,
	Tag,
	Tooltip,
	Typography,
	message,
} from 'antd';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

import { useParams } from 'react-router';
import {
	useGetOrderByIdQuery,
	useSetOrderInspectionSampleLabelingMutation,
} from '../../../redux/api/order';
import { CardItem } from '../CardItemDetail';
import { ShopShipDate } from '../../../components/ShopShipDate';
import { soonestShopLine } from '../../../components/ShopShipDate/soonestShopLine';
import { Lineitem } from '../../../interfaces/Ordel.Interface';
import { parseISO } from 'date-fns';

import { STAFF_PORTAL_URL } from '../../../config/constants';
import { Link } from 'react-router-dom';

import Header from '../../../components/Header';
import Loading from '../../../components/Loading/index.tsx';
import { getOrderStatusTagColor, getTotalPartsQty } from '../../../utils';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useHasMultipleBuildings } from '../../../hooks/useHasMultipleBuildings';
import { useCanViewRevenue } from '../../../hooks/useCanViewRevenue';
import { useCanManageInspectionSampleLabeling } from '../../../hooks/useCanManageInspectionSampleLabeling';
import BuildingTag from '../../../components/BuildingTag';
import InternalDraftBanner from '../InternalDraftBanner';
import OrderProcessesApply from '../../../components/ProcessCatalog/OrderProcessesApply';

const { Title, Text } = Typography;

const LABELING_TOOLTIP =
	'Every inspected sample must be bagged and labeled with its sample number and the operator’s initials. This applies to all of the order’s line items; a single line item can still be changed on its own detail page afterwards.';

/** Operators work this page for material reservations, and a stray click on a
 * live checkbox silently changes what quality and delivery will demand of a
 * job. Read-only rather than hidden: they still need to see the requirement. */
const LABELING_READ_ONLY_TOOLTIP = `${LABELING_TOOLTIP} Only Shop Managers can change this.`;

const OrderDetails = () => {
	const { id } = useParams();
	const hasMultipleBuildings = useHasMultipleBuildings();
	const canViewRevenue = useCanViewRevenue();
	const canManageLabeling = useCanManageInspectionSampleLabeling();

	const { data, isLoading, refetch } = useGetOrderByIdQuery(
		{ id: id as string },
		{ skip: !id, refetchOnMountOrArgChange: true }
	);

	const [form] = Form.useForm();

	const [setInspectionSampleLabeling, { isLoading: isSavingLabeling }] =
		useSetOrderInspectionSampleLabelingMutation();

	async function handleLabelingChange(e: CheckboxChangeEvent) {
		if (!data) return;

		try {
			await setInspectionSampleLabeling({
				id: data.id,
				required: e.target.checked,
			}).unwrap();
			refetch();
		} catch {
			message.error('Could not update Inspection Sample Labeling.');
		}
	}

	const uniqueBuildings = (() => {
		if (!data?.line_items) return [];
		const seen = new Map<string, { id: string; name: string }>();
		for (const item of data.line_items) {
			if (item.building && !seen.has(item.building.id)) {
				seen.set(item.building.id, item.building as { id: string; name: string });
			}
		}
		return Array.from(seen.values());
	})();

	const compareDates = (a: string, b: string): number => {
		const dateA = parseISO(a);
		const dateB = parseISO(b);
		return dateA < dateB ? -1 : dateA > dateB ? 1 : 0;
	};

	return (
		<>
			<Header
				title="Order Details"
				breadcrumbItems={[
					{ title: <Link to="/">Home</Link> },
					{ title: <Link to="/orders">Orders</Link> },
					{ title: 'Order Details' },
				]}
			/>

			{!data || isLoading ? (
				<Loading loading={true} />
			) : (
				<main
					data-testid="page-fastlane-order-details"
					data-ready="true"
					data-order-id={data.id}
				>
					{data.order_type === 'internal' && data.is_draft && (
						<InternalDraftBanner orderId={data.id} />
					)}
					<Row gutter={[8, 16]}>
						<Col xs={24} span={24}>
							<Card>
								<div className="flex flex-col sm:flex-row w-full gap-x-12 gap-y-6 mb-5 shrink-0 flex-wrap">
									<Flex vertical align="start">
										<span className="text-slate-400">Order Name</span>
										<Title level={5}>{data?.name}</Title>
									</Flex>

									<Flex vertical align="start">
										<span className="text-slate-400">Number of Line Items</span>
										<span>{data?.line_items?.length}</span>
									</Flex>

									<Flex vertical align="start">
										<span className="text-slate-400">Total part quantities</span>
										{data &&
										getTotalPartsQty(data.line_items) !==
											data.line_items.reduce((a, b) => a + b.quantity, 0) ? (
											<div className="flex flex-row items-baseline gap-1">
												<Text delete>
													{data.line_items.reduce((a, b) => a + b.quantity, 0)}
												</Text>
												<Text>
													<ArrowRightOutlined style={{ fontSize: '0.7rem' }} />
												</Text>
												<Text strong mark>
													{getTotalPartsQty(data.line_items)}
												</Text>
											</div>
										) : (
											<span>
												{data?.line_items?.reduce((a, b) => a + b.quantity, 0)}
											</span>
										)}
									</Flex>

									<Flex vertical align="start">
										<span className="text-slate-400">Soonest Due Date</span>
										<span>
											{data?.line_items
												? data.line_items
														.map((item) => item.required_part_ship_date)
														.sort(compareDates)[0]
												: ''}
										</span>
									</Flex>

									<Flex vertical align="start">
										<span className="text-slate-400">Shop Due Date</span>
										<span>
											{(() => {
												const shopLine = soonestShopLine(data?.line_items);
												const original = shopLine?.revised_partner_ship_date
													? shopLine.required_partner_ship_date
													: shopLine?.shop_due_date ||
														shopLine?.required_partner_ship_date;
												return (
													<ShopShipDate
														original={original}
														revised={shopLine?.revised_partner_ship_date}
													/>
												);
											})()}
										</span>
									</Flex>

									{data.order_type === 'internal' ? (
										<Flex vertical align="start">
											<span className="text-slate-400">Category</span>
											{data.internal_category ? (
											<Tag
												data-testid="fastlane-internal-order-category"
												color="purple"
												style={{ textTransform: 'capitalize' }}
											>
												{data.internal_category}
											</Tag>
											) : (
												<Typography.Text type="secondary">—</Typography.Text>
											)}
										</Flex>
									) : (
										<Flex vertical align="start">
											<span className="text-slate-400">Sales Order</span>
											<Link
												to={`${STAFF_PORTAL_URL}/sales-order/${data?.partsbadger_api_id}/`}
												target="_blank"
												rel="noreferrer"
											>
												{data?.name}
											</Link>
										</Flex>
									)}

									<Flex vertical align="start">
										<span className="text-slate-400">Order Status</span>
										<div>
											<Tag color={getOrderStatusTagColor(data?.status)}>
												{data?.status}
											</Tag>
											{data.is_draft && <Tag color="gold">Draft</Tag>}
										</div>
									</Flex>

									{hasMultipleBuildings && (
										<Flex vertical align="start">
											<span className="text-slate-400">Buildings</span>
											{uniqueBuildings.length > 0 ? (
												<div className="flex flex-wrap gap-1 mt-1">
													{uniqueBuildings.map((b) => (
														<BuildingTag key={b.id} name={b.name} showIcon={false} />
													))}
												</div>
											) : (
												<Typography.Text type="secondary">—</Typography.Text>
											)}
										</Flex>
									)}
								</div>

								{data.order_type === 'internal' && (
									<div className="w-full mb-5">
										<Flex vertical align="start">
											<span className="text-slate-400">Internal Notes</span>
											{data.internal_notes ? (
											<Typography.Paragraph
												data-testid="fastlane-internal-order-notes"
													style={{ whiteSpace: 'pre-wrap', marginBottom: 0 }}
												>
													{data.internal_notes}
												</Typography.Paragraph>
											) : (
												<Typography.Text type="secondary">—</Typography.Text>
											)}
										</Flex>
									</div>
								)}

								<div className="flex flex-col sm:flex-row w-full gap-x-12 gap-y-6 mb-5 shrink-0 flex-wrap">
									<Flex vertical align="start">
										<span className="text-slate-400">Material Cost</span>
										<span>
											{data?.material_cost != null && data?.material_cost !== ''
												? `$${Number(data.material_cost).toFixed(2)}`
												: '—'}
										</span>
									</Flex>
									{/* Material Cost % temporarily removed per requirements
									<Flex vertical align="start">
										<span className="text-slate-400">Material Cost %</span>
										<span>
											{data?.material_cost_percent != null &&
											data?.material_cost_percent !== ''
												? `${Number(data.material_cost_percent).toFixed(2)}%`
												: '—'}
										</span>
									</Flex>
									*/}
									{canViewRevenue && (
										<Flex vertical align="start">
											<span className="text-slate-400">Revenue</span>
											<span>
												{data?.revenue != null && data?.revenue !== ''
													? `$${Number(data.revenue).toFixed(2)}`
													: '—'}
											</span>
										</Flex>
									)}
								</div>

								<div className="flex flex-col sm:flex-row w-full gap-x-12 gap-y-6 shrink-0 flex-wrap">
									<Flex vertical align="start" className="min-w-0">
										<span className="text-slate-400">
											Additional Requirements
										</span>
										<span>
											{data?.additional_requirements?.length > 0
												? data.additional_requirements.join(', ')
												: 'None'}
										</span>
									</Flex>

									<Flex vertical align="start" className="min-w-0">
										<span className="text-slate-400">In House Requirements</span>
										<span>
											{data?.in_house_requirements
												? data.in_house_requirements
												: 'None'}
										</span>
									</Flex>

									<Flex vertical align="start" className="min-w-0">
										<span className="text-slate-400">
											Inspection Sample Labeling
										</span>
										<Tooltip
											title={
												canManageLabeling
													? LABELING_TOOLTIP
													: LABELING_READ_ONLY_TOOLTIP
											}
										>
											<Checkbox
												checked={!!data?.requires_inspection_sample_labeling}
												disabled={!canManageLabeling || isSavingLabeling}
												onChange={handleLabelingChange}
											>
												Required for this order
											</Checkbox>
										</Tooltip>
									</Flex>

									{/* Set here rather than per line item: one customer usually
									    wants their parts handled and cleaned one way. The line
									    item pickers are for the exception. */}
									<OrderProcessesApply
										orderId={data.id}
										lineItemCount={data.line_items?.length ?? 0}
										handlingSpec={data.handling_spec}
										cleaningSpec={data.cleaning_spec}
										refetch={refetch}
									/>
								</div>
							</Card>
						</Col>

						<Col span={24}>
							<Form name="line_item_material_form" form={form}>
								{data?.line_items?.map((item: Lineitem, index) => (
									<CardItem
										key={index}
										lineItem={item}
										refetchOrder={refetch}
										isLoading={isLoading}
										status_order={data.status_fastlane}
										ready_for_production={data.ready_for_production}
										orderIsDraft={data.is_draft}
									/>
								))}
							</Form>
						</Col>
					</Row>
				</main>
			)}
		</>
	);
};

export default OrderDetails;
